import React, { useEffect, useMemo, useState } from 'react';
import {
  Clock3,
  Gift,
  History,
  Tag,
  X,
} from 'lucide-react';
import ModalShell from './ModalShell';
import { CompactListSkeleton } from './Skeleton';
import useModalBackButton from '../hooks/useModalBackButton';
import * as activityApi from '../services/activityApi';
import { formatTimezoneDate } from '../utils/timezone';
import { haptic } from '../utils/vibration';

const FIELD_LABELS = {
  name: 'Название',
  description: 'Описание',
  client_type: 'Тип клиента',
  discount_type: 'Тип скидки',
  discount_value: 'Размер скидки',
  date_from: 'Дата начала',
  date_to: 'Дата окончания',
  status: 'Статус',
  max_discount_amount: 'Макс. сумма скидки',
  min_order_amount: 'Мин. сумма заказа',
  min_items_count: 'Мин. количество предметов',
  every_nth_item_free: 'Каждая N-я вещь бесплатно',
  scope: 'Область применения',
  service_type: 'Тип услуги',
  service_name: 'Услуга',
  group_name: 'Группа услуг',
  active: 'Активность',
  combinable: 'Суммируется со скидками',
  allowed_roles: 'Кто может применять',
  auto_apply_enabled: 'Автоприменение',
  priority: 'Приоритет',
  system_code: 'Системный код',
  template_type: 'Тип шаблона',
  time_from: 'Время с',
  time_to: 'Время до',
  days_of_week: 'Дни недели',
  planned_change: 'Запланированное изменение',
  banner_url: 'Баннер',
  banner_color: 'Цвет баннера',
};

const CLIENT_TYPE_LABELS = {
  b2c: 'Физ. лица',
  b2b: 'Организации',
};

const DISCOUNT_TYPE_LABELS = {
  percent: 'Процентная',
  fixed: 'Фиксированная',
};

const PROMOTION_STATUS_LABELS = {
  draft: 'Черновик',
  active: 'Активна',
  scheduled: 'Запланирована',
  expired: 'Истекла',
  archived: 'В архиве',
};

const SCOPE_LABELS = {
  order: 'На весь заказ',
  service: 'На услугу',
  group: 'На группу',
  category: 'На категорию',
};

const SERVICE_TYPE_LABELS = {
  washing: 'Стирка',
  cleaning: 'Химчистка',
  extra_washing: 'Доп. стирка',
  extra_cleaning: 'Доп. чистка',
  extra_order: 'Доп. услуги заказа',
};

const ROLE_LABELS = {
  all: 'Все',
  dispatcher: 'Диспетчер+',
  admin: 'Только админ',
};

const TEMPLATE_TYPE_LABELS = {
  standard: 'Основная',
  social: 'Социальная',
  wholesale: 'Оптовая',
  no_delivery: 'Без доставок',
  daily: 'Ежедневная',
  personal: 'Персональная',
};

const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const HISTORY_CONFIG = {
  discount_template: {
    title: 'История скидки',
    entityName: 'скидки',
    entityLabel: 'Скидка',
    emptyText: 'Для этой скидки ещё нет сохранённых изменений.',
    loadHistory: activityApi.getDiscountTemplateChangeHistory,
    icon: Tag,
  },
  promotion: {
    title: 'История акции',
    entityName: 'акции',
    entityLabel: 'Акция',
    emptyText: 'Для этой акции ещё нет сохранённых изменений.',
    loadHistory: activityApi.getPromotionChangeHistory,
    icon: Gift,
  },
};

const HIDDEN_KEYS = new Set([
  'id',
  'service_id',
  'group_id',
  'created_date',
  'updated_date',
  'created_by',
  'updated_by',
  'creator_name',
  'is_active_now',
]);

const safeStringify = (value) => {
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const normalizeDaysOfWeek = (value) => {
  if (Array.isArray(value)) return value.map(Number).filter(Number.isFinite).sort((left, right) => left - right);
  return [];
};

const normalizePlannedChange = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return {
    discount_type: value.discount_type || null,
    discount_value: value.discount_value ?? null,
    max_discount_amount: value.max_discount_amount ?? null,
    min_order_amount: value.min_order_amount ?? null,
    min_items_count: value.min_items_count ?? null,
    effective_from: value.effective_from || null,
  };
};

const normalizeComparableValue = (fieldKey, value) => {
  if (fieldKey === 'days_of_week') return normalizeDaysOfWeek(value);
  if (fieldKey === 'planned_change') return normalizePlannedChange(value);
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim();
  return value;
};

const hasMeaningfulDifference = (fieldKey, oldValue, newValue) => (
  safeStringify(normalizeComparableValue(fieldKey, oldValue)) !== safeStringify(normalizeComparableValue(fieldKey, newValue))
);

const formatBoolean = (value) => (value ? 'Да' : 'Нет');

const formatDateOnly = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '—';
  const [year, month, day] = rawValue.split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return rawValue;
  return new Date(year, month - 1, day).toLocaleDateString('ru-RU');
};

const formatDiscountValue = (discountType, discountValue) => {
  if (discountValue === null || discountValue === undefined || discountValue === '') return '—';
  const suffix = discountType === 'fixed' ? ' ₽' : '%';
  return `${discountValue}${suffix}`;
};

const formatValue = (fieldKey, value, fullRecord = {}) => {
  if (value === null || value === undefined || value === '') return '—';

  if (fieldKey === 'client_type') return CLIENT_TYPE_LABELS[String(value).trim()] || String(value);
  if (fieldKey === 'discount_type') return DISCOUNT_TYPE_LABELS[String(value).trim()] || String(value);
  if (fieldKey === 'status') return PROMOTION_STATUS_LABELS[String(value).trim()] || String(value);
  if (fieldKey === 'scope') return SCOPE_LABELS[String(value).trim()] || String(value);
  if (fieldKey === 'service_type') return SERVICE_TYPE_LABELS[String(value).trim()] || String(value);
  if (fieldKey === 'allowed_roles') return ROLE_LABELS[String(value).trim()] || String(value);
  if (fieldKey === 'template_type') return TEMPLATE_TYPE_LABELS[String(value).trim()] || String(value);
  if (fieldKey === 'active' || fieldKey === 'auto_apply_enabled') return formatBoolean(Boolean(value));
  if (fieldKey === 'combinable') return formatBoolean(Boolean(value));
  if (fieldKey === 'discount_value') return formatDiscountValue(fullRecord.discount_type, value);
  if (fieldKey === 'date_from' || fieldKey === 'date_to') return formatDateOnly(value);
  if (fieldKey === 'min_order_amount' || fieldKey === 'max_discount_amount') return `${value} ₽`;
  if (fieldKey === 'days_of_week') {
    const normalized = normalizeDaysOfWeek(value);
    return normalized.length ? normalized.map((day) => WEEKDAY_LABELS[day] || String(day)).join(', ') : '—';
  }
  if (fieldKey === 'planned_change') {
    const planned = normalizePlannedChange(value);
    if (!planned) return '—';
    const parts = [
      formatDiscountValue(planned.discount_type, planned.discount_value),
      planned.effective_from ? `с ${formatTimezoneDate(planned.effective_from, false)}` : null,
    ].filter(Boolean);
    return parts.join(' · ') || '—';
  }
  if (typeof value === 'boolean') return formatBoolean(value);
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.trim() || '—';
  return safeStringify(value);
};

const buildChangeRows = (oldValue, newValue) => {
  const before = oldValue && typeof oldValue === 'object' && !Array.isArray(oldValue) ? oldValue : {};
  const after = newValue && typeof newValue === 'object' && !Array.isArray(newValue) ? newValue : {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));

  return keys
    .filter((key) => !HIDDEN_KEYS.has(key))
    .filter((key) => hasMeaningfulDifference(key, before[key], after[key]))
    .map((key) => ({
      key,
      label: FIELD_LABELS[key] || key,
      oldValue: before[key],
      newValue: after[key],
      oldRecord: before,
      newRecord: after,
    }));
};

const summarizeLog = (log, changeRows, config) => {
  const targetLabel = (config?.entityLabel || 'Скидка').toLowerCase();
  const targetName = log.new_value_data?.name || log.old_value_data?.name || log.target_name || `#${log.entity_id}`;
  if (log.action === 'create') {
    return `Создана ${targetLabel} «${targetName}»`;
  }
  if (log.action === 'delete') {
    return `Удалена ${targetLabel} «${targetName}»`;
  }
  if (changeRows.length > 0) {
    return changeRows
      .slice(0, 3)
      .map((row) => `${row.label}: ${formatValue(row.key, row.newValue, row.newRecord)}`)
      .join(' • ');
  }
  return log.summary || 'Изменения сохранены';
};

const DiscountActivityModal = ({ open, onClose, subject, subjectType = 'discount_template' }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const config = HISTORY_CONFIG[subjectType] || HISTORY_CONFIG.discount_template;
  const HistoryIcon = config.icon || Tag;

  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open || !subject?.id) return undefined;

    let cancelled = false;

    const loadHistory = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await config.loadHistory(subject.id, 50, 0);
        if (cancelled) return;
        setLogs(Array.isArray(data) ? data : []);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || `Не удалось загрузить историю ${config.entityName}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [config, open, subject?.id]);

  const visibleLogs = useMemo(() => (
    logs.filter((log) => log.action !== 'update' || buildChangeRows(log.old_value_data, log.new_value_data).length > 0)
  ), [logs]);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="sheet"
      expandable={true}
      panelClassName="max-h-[88vh] overflow-hidden rounded-t-xl sm:max-w-3xl sm:rounded-xl"
    >
      <div className="flex max-h-[88vh] flex-col">
        <div className="border-b border-gray-200 px-4 py-4 dark:border-gray-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">{config.title}</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {subject?.name || `${config.entityLabel} #${subject?.id}`}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                haptic('close');
                onClose();
              }}
              className="rounded-lg bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-4 py-4">
          {loading ? (
            <CompactListSkeleton count={4} />
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
              {error}
            </div>
          ) : visibleLogs.length === 0 ? (
            <div className="py-10 text-center">
              <History className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
              <div className="mt-3 text-sm font-medium text-gray-900 dark:text-white">История пока пуста</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {config.emptyText}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleLogs.map((log) => {
                const changeRows = buildChangeRows(log.old_value_data, log.new_value_data);
                const summary = summarizeLog(log, changeRows, config);

                return (
                  <div
                    key={log.id}
                    className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
                        <HistoryIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {log.user_name || `Пользователь #${log.user_id}`}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400">{log.action_label}</span>
                        </div>

                        <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Clock3 className="h-3.5 w-3.5" />
                          <span>{formatTimezoneDate(log.created_date, true)}</span>
                        </div>

                        <div className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200">
                          {summary}
                        </div>

                        {changeRows.length > 0 ? (
                          <details className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/40">
                            <summary className="cursor-pointer list-none text-xs font-medium text-gray-700 dark:text-gray-200">
                              Что изменили
                            </summary>
                            <div className="mt-2 space-y-2">
                              {changeRows.map((row) => (
                                <div key={row.key} className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-gray-900">
                                  <div className="font-medium text-gray-500 dark:text-gray-400">{row.label}</div>
                                  <div className="mt-1 grid gap-1 sm:grid-cols-2">
                                    <div className="min-w-0 text-gray-600 dark:text-gray-300">
                                      <span className="mb-1 block text-gray-400">Было:</span>
                                      <span className="whitespace-pre-wrap break-words">
                                        {formatValue(row.key, row.oldValue, row.oldRecord)}
                                      </span>
                                    </div>
                                    <div className="min-w-0 text-gray-900 dark:text-gray-100">
                                      <span className="mb-1 block text-gray-400">Стало:</span>
                                      <span className="whitespace-pre-wrap break-words">
                                        {formatValue(row.key, row.newValue, row.newRecord)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default DiscountActivityModal;
